import { SORT_COLUMN_BY_TOGGLE_MODE, TOGGLE_MODE} from './AllocationAndDrillDownPanel/DrillDownChart/drillDownChartHelpers.js';
import { UPLOAD, PERSONNEL, NONPERSONNEL, REVENUE, POSITIVE, NEGATIVE, USER_GROUP, TABLE } from '../../static/keyWords.js';
import { USER_ROLE } from '../../static/constants.js';
import { messageManager } from '../System/messageManager/messageManager.js';
import { getMoneyObj } from '../reusable/MoneyJsHelpers.js';
import { SESSION_STORAGE_KEYS } from '../../static/localStorageStrings.js';

export const COST_ALLOC_PAGE_LENGTH = 30;
export const COST_ALLOCATION_TOGGLE_BUTTON_LABELS = ['%', '$'];
export const ORDER_BY_CLAUSE_DEFAULT_ITEMS_CARD = { name: 'objectCategories', direction: 'asc' };


export const getCostType = (selectedBar) => {
  let stackLevels = selectedBar.path.split('---');
  let costTypeLevelIndex = stackLevels.findIndex(level => level === PERSONNEL || level === NONPERSONNEL || level === REVENUE);
  let costType;
  // if there is no cost type stack level
  // or it is lower than the selected bar 
  // and cannot be determined
  // default to personnel
  if (costTypeLevelIndex < 0) {
    return PERSONNEL;
  }
  else costType = stackLevels[costTypeLevelIndex];
  // revenue defaults to non personnel
  if (costType === REVENUE) costType = NONPERSONNEL;
  return costType;
};

export const getValidationPageNewUploadButton = (handleNewUpload) => {
  return [
    {
      id: 'upload',
      name: 'New upload',
      variant: 'text',
      icon: 'upload',
      onClickFunction: () => handleNewUpload(),
    },
  ];
};

export const getCostAllocationHeaderButtons = (setIsOpenUploadDialog, handleExport, user, isExportButtonDisabled, nameConventions) => {
  return [
    {
      id: 'export',
      name: 'Export',
      variant: 'text',
      icon: 'export',
      onClickFunction: handleExport,
      disabled: isExportButtonDisabled,
      tooltip: messageManager(nameConventions).FILE_DOWNLOAD_IN_PROGRESS_MESSAGE,
    },
    {
      id: 'upload',
      name: 'Upload',
      variant: 'raised',
      onClickFunction: () => setIsOpenUploadDialog(true),
      disabled: user?.role !== USER_ROLE.ADMIN || user?.isViewOnly,
      tooltip: 'Unavailable at the moment.'
    },
  ];
};

export const getButtonStateCondition = (fireUnsavedChangesDialog, combinedProgramCost, combinedTotalCost, view, mistakes, actionRows, previewPercent, selectedItems, selectedItemAllocations) => {
  if (view === UPLOAD) {
    if (fireUnsavedChangesDialog || actionRows) {
      if (mistakes && (mistakes[0] + mistakes[1] > 0)) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  } else {
    if (selectedItemAllocations && selectedItemAllocations.length === 0) {
      return true;
    }
    if (fireUnsavedChangesDialog) {
      if (Math.abs(combinedProgramCost) > Math.abs(combinedTotalCost) || previewPercent > 100) {
        return true;
      } else {
        return false;
      }
    } else {
      if (view === TABLE && selectedItems.length > 1 && selectedItems.some(item => item.allocation_percent > 0)) {
        return false;
      } else {
        return true;
      }
    }
  }
};

const SORT_MODE = {
  LABEL_ASC: ['label', 'ASC'],
  LABEL_DESC: ['label', 'DESC'],
  VALUE_ASC: ['percent', 'ASC'],
  VALUE_DESC: ['percent', 'DESC'],
};

const getSortColumnByToggleMode = (toggleMode) => SORT_COLUMN_BY_TOGGLE_MODE[toggleMode] || SORT_COLUMN_BY_TOGGLE_MODE[TOGGLE_MODE.ALLOCATIONS];

const getSortModeForToggleMode = (sortMode, toggleMode) => {
  if (sortMode[0] === 'label') return [...sortMode];
  return [getSortColumnByToggleMode(toggleMode)[0], sortMode[1]];
};

const handleSort = (setSortBy,updateChartData,toggleMode,newSortMode,currentFilters) => {
  const sortMode = getSortModeForToggleMode(newSortMode, toggleMode);
  setSortBy(sortMode);
  updateChartData(sortMode,currentFilters);
};

const sortByLabelAsc = (setSortBy,updateChartData,toggleMode, currentFilters) => handleSort(setSortBy,updateChartData,toggleMode,SORT_MODE.LABEL_ASC, currentFilters);
const sortByLabelDesc = (setSortBy,updateChartData,toggleMode, currentFilters) => handleSort(setSortBy,updateChartData,toggleMode,SORT_MODE.LABEL_DESC, currentFilters);
const sortByValueAsc = (setSortBy,updateChartData,toggleMode, currentFilters) => handleSort(setSortBy,updateChartData,toggleMode,SORT_MODE.VALUE_ASC, currentFilters);
const sortByValueDesc = (setSortBy,updateChartData,toggleMode, currentFilters) => handleSort(setSortBy,updateChartData,toggleMode,SORT_MODE.VALUE_DESC, currentFilters);

const SORT_OPTION_IDS = {
  LABEL_ASC: 'labelAsc',
  LABEL_DESC: 'labelDesc',
  VALUE_ASC: 'valueAsc',
  VALUE_DESC: 'valueDesc',
};

export let sortOptions = [
  {
    id: SORT_OPTION_IDS.LABEL_ASC,
    label: 'Sort by label A-Z',
    value: sortByLabelAsc,
    sortMode: SORT_MODE.LABEL_ASC,
    orderBy: {
      name: 'objectCategories',
      direction: 'asc'
    }
  },
  {
    id: SORT_OPTION_IDS.LABEL_DESC,
    label: 'Sort by label Z-A',
    value: sortByLabelDesc,
    sortMode: SORT_MODE.LABEL_DESC,
    orderBy: {
      name: 'objectCategories',
      direction: 'desc'
    }
  },
  {
    id: SORT_OPTION_IDS.VALUE_ASC,
    label: 'Sort by value low-high',
    value: sortByValueAsc,
    sortMode: SORT_MODE.VALUE_ASC,
    orderBy: {
      name: 'itemTotalCost',
      direction: 'asc'
    }
  },
  {
    id: SORT_OPTION_IDS.VALUE_DESC,
    label: 'Sort by value high-low',
    value: sortByValueDesc,
    sortMode: SORT_MODE.VALUE_DESC,
    orderBy: {
      name: 'itemTotalCost',
      direction: 'desc'
    }
  },
];

export const saveSortSelection = (sortOptionId) => {
  sessionStorage.setItem(SESSION_STORAGE_KEYS.COST_ALLOCATION_SORT, sortOptionId);
};

const getSavedSortOption = () => {
  const savedSortOptionId = sessionStorage.getItem(SESSION_STORAGE_KEYS.COST_ALLOCATION_SORT);
  return sortOptions.find((option) => option.id === savedSortOptionId);
};

export const getInitialSortBy = (toggleMode) => {
  const savedSortOption = getSavedSortOption();
  if (!savedSortOption) return [...getSortColumnByToggleMode(toggleMode)];
  return getSortModeForToggleMode(savedSortOption.sortMode, toggleMode);
};

export const getInitialOrderBy = () => getSavedSortOption()?.orderBy || ORDER_BY_CLAUSE_DEFAULT_ITEMS_CARD;

export const getIdsAndCostCentersOfSelectedItems = (selectedItems) => {
  let itemIds = [];
  let costCenterIds = new Set();
  selectedItems.forEach((item) => {
    itemIds.push(item.itemid);
    costCenterIds.add(item.cc_id);
  });
  return { itemIds, costCenterIds };
};

const getAllPossibleStackLevels = (nameConventions) => {
  return {
    COST_TYPE: 'Cost Type',
    ACCOUNT: 'Account',
    ACCT_TYPE: 'Account type',
    FUND: 'Fund',
    PROG_NAME: 'ProgName',
    NAME: 'Name',
    COST_CENTER: nameConventions[USER_GROUP].alias,
  };
};

export const getAllStackOptions = (orgHierarchy, nameConventions) => {
  let objCategoryOptions = [];
  const numOfObjCategoryOptions = Math.min(orgHierarchy.nonPersonnelCategories.length, orgHierarchy.personnelCategories.length);
  for (let i = 0; i < numOfObjCategoryOptions; i += 1) {
    objCategoryOptions.push(`${orgHierarchy.nonPersonnelCategories[i]}/${orgHierarchy.personnelCategories[i]}`);
  }
  let orgLevelOptions = orgHierarchy.organizationLevels;
  return {
    ...getAllPossibleStackLevels(nameConventions),
    ORG_LEVELS: orgLevelOptions,
    OBJ_CATEGORIES: objCategoryOptions,
  };
};

export const getDefaultStack = (allStackLevelOptions, toggleMode) => {
  if (toggleMode === TOGGLE_MODE.ALLOCATIONS)
    return [allStackLevelOptions.ACCT_TYPE, allStackLevelOptions.COST_TYPE, allStackLevelOptions.ORG_LEVELS[0], allStackLevelOptions.ORG_LEVELS[1]].filter(Boolean);
  else return [allStackLevelOptions.ACCT_TYPE, allStackLevelOptions.PROG_NAME, allStackLevelOptions.COST_TYPE];
};

export const getAllPossibleKeys = (allStackLevelOptions) => {
  if (!allStackLevelOptions) return [];
  let allPossibleKeys = [];
  Object.values(allStackLevelOptions).forEach((val) => {
    if (Array.isArray(val)) allPossibleKeys.push(...val);
    else allPossibleKeys.push(val);
  });

  return allPossibleKeys;
};

export const removeNameConventions = (selected, costCenterNameConvention) => {
  return selected.map((value) => {
    if (value === costCenterNameConvention) {
      value = USER_GROUP;
    }
    return value;
  });
};

export const applyNameConventions = (selected, costCenterNameConvention) => {
  return selected.map((value) => {
    if (value === 'Cost Center') {
      value = costCenterNameConvention;
    }
    return value;
  });
};

export const getConcatenatedObjectLevels = (item) => {
  const firstObjectCategoryLevel = item.object_categories_json && item.object_categories_json[0].name;
  const lastObjectCategoryLevel = item.object_categories_json && item.object_categories_json[item.object_categories_json.length - 1].name;
  return item.object_categories_json && item.object_categories_json.length > 1 
    ? firstObjectCategoryLevel + ' ' + lastObjectCategoryLevel 
    : firstObjectCategoryLevel;
};

export const getAllocationPanelCardButtons = (selectAll, disableSelectAll, handleNonFullyAllocatedCheckboxChange, handleClearSelected, showTableWithAllocations, totalItemsCount, isNonFullyAllocatedChecked, toggleMode, user, positiveOnly, handlePositiveOnly, isCurrentPageFullySelected) => [
  {
    name: 'Select all',
    type: 'checkbox',
    onChange: (e) => selectAll(e.target.checked),
    disabled: disableSelectAll,
    slot: 'start',
    show: (totalItemsCount > 0 && totalItemsCount <= 100) && (toggleMode === TOGGLE_MODE.ALLOCATIONS ? true : false),
    checked: isCurrentPageFullySelected,
  },
  {
    name: 'Allocations below 100%',
    type: 'checkbox',
    onChange: handleNonFullyAllocatedCheckboxChange,
    disabled: false,
    slot: 'start',
    show: true,
    checked: isNonFullyAllocatedChecked,
  },
  {
    name: 'Positive or negative costs only',
    type: 'buttonToggle',
    options: [POSITIVE, NEGATIVE],
    selected: positiveOnly ? POSITIVE : NEGATIVE,
    onChange: handlePositiveOnly,
    outlined: true,
    show: true,
  },
  {
    name: 'Clear all',
    type: 'button',
    variant: 'text',
    onClick: handleClearSelected,
    slot: 'end',
    show: toggleMode === TOGGLE_MODE.ALLOCATIONS ? true : false,
  },
  {
    name: 'Make allocations',
    type: 'button',
    variant: 'filled',
    onClick: showTableWithAllocations,
    slot: 'end',
    show: toggleMode === TOGGLE_MODE.ALLOCATIONS ? true : false,
    disabled: user?.isViewOnly,
  }
];


//##########################################################################
export const getUnifiedPrograms = (itemAllocations) => {
  return itemAllocations
    .filter(
      (allocation, index, self) =>
        index === self.findIndex((a) => a.programId === allocation.programId)
    );
};

export const getAllItemAllocations = (selectedItems) => structuredClone(selectedItems)
  .map((item) => item.allocations)
  .flat();

export const getAllocationsWithCalculations = (unifiedPrograms, allItemAllocations) => {
  return unifiedPrograms.map((program) => {
    return {
      programId: program.id,
      programName: program.name,
      programNumber: program.number,
      description: program.description,
      allocationsToItems: allItemAllocations.filter((alloc) => alloc.programId === program.id).map((alloc) => ({
        itemId: alloc.itemId,
        allocId: alloc.id,
        programId: alloc.programId,
      })),
      serviceType: program.serviceType,
      costCenter: program.costCenter,
      department: program.organizationLevels ? program.organizationLevels.split('--')[0] : null,
      division: program.organizationLevels ? program.organizationLevels.split('--')[1] : null,
      byFte: program.fte,
      dollars: allItemAllocations
        .filter((alloc) => alloc.programId === program.id)
        .reduce(
          (sum, alloc) => sum.add(getMoneyObj(alloc.allocatedDollars)),
          getMoneyObj(0)
        ),
      percent: getMoneyObj(0), // to be calculated later
    };
  });
};

export const getDollarsArray = (allocationsWithCalculations) => allocationsWithCalculations.map(
  (prog) => prog.dollars.amount
);

export const allocatePercents = (dollarsArray, combinedTotalCostOfItems) =>  getMoneyObj(100).allocate([
  ...dollarsArray,
  combinedTotalCostOfItems * 100 -
        dollarsArray.reduce(
          (sum, curr) => sum.add(getMoneyObj(curr)),
          getMoneyObj(0)
        ),
].map(value => Math.abs(value)));

export const calculatePreviewPercents = (selectedItems) => {
  const totalAllocatedAmountMoney = selectedItems.reduce((sum, item) => { 
    return sum.add(getMoneyObj(Number(item.program_cost)));
  }, getMoneyObj(0));
  const totalItemCostMoney = selectedItems.reduce((sum, item) => { 
    return sum.add(getMoneyObj(Number(item.total_cost)));
  }, getMoneyObj(0));

  return Number(
    (
      (Math.abs(totalAllocatedAmountMoney.amount) / Math.abs(totalItemCostMoney.amount)) *
      100
    ).toFixed(2)
  );
};

export const assignPercentsToAllocations = (allocationsWithCalculations, percentsArray) => {
  allocationsWithCalculations.forEach((prog, index) => {
    prog.percent = percentsArray[index];
  });
};

export const mapNumberValuesOfAllocations = (allocationsWithCalculations, allocationMode) => {
  const result = allocationsWithCalculations.map((prog) => {
    return {
      ...prog,
      dollars: prog.dollars.amount / 100,
      percent: prog.percent.amount / 100,
      uniqueId: `${prog.programId}-${Math.random().toString(36).substr(2, 9)}`,
      initialOrEditedAllocation: true, //put this here so when we add additional programs without saving and manipulate and then we remove the additional( yes we do all we can to break things) will be easy to filter
    };
  }).sort((a, b) => {
    if (allocationMode === COST_ALLOCATION_TOGGLE_BUTTON_LABELS[0]) {
      return b.percent - a.percent;
    } else {
      return Math.abs(b.dollars) - Math.abs(a.dollars);
    }
  });

  return result;
};

export const mapAllocationsToSelectedItems = (selectedItems, data, allItemAllocations) => {
  return selectedItems.map(item => ({
    ...item,
    original_allocation_percent: item.allocation_percent,
    allocations: data.map(program => ({
      id: allItemAllocations.find(
        (alloc) => alloc.programId === program.id && alloc.itemId === item.itemid
      )?.allocId || null,
      programId: program.id,
      programName: program.name,
      programNumber: program.number,
      serviceType: program.serviceType,
      costCenter: program.costCenter,
      department: program.organizationLevels ? program.organizationLevels.split('--')[0] : null,
      division: program.organizationLevels ? program.organizationLevels.split('--')[1] : null,
      byFte: program.fte,
      dollars: Number(allItemAllocations.find(
        (alloc) => alloc.programId === program.id && alloc.itemId === item.itemid
      )?.allocatedDollars || 0),
      percent: Number(allItemAllocations.find(
        (alloc) => alloc.programId === program.id && alloc.itemId === item.itemid
      )?.allocatedPercent || 0),
    }
    )),
  }));
};

export const calculateOriginalAllocationPercent = (selectedItems) => {
  selectedItems.forEach(item => {
    const total = (item.allocations || []).reduce((percentSum, allocation) => {
      return percentSum.add(getMoneyObj(allocation.percent));
    }, getMoneyObj(0));

    item.original_allocation_percent = Number((total.amount / 100).toFixed(2));
  });
};

export const recalculateItemProgramCostForAllocationsTable = (selectedItems) => {
  selectedItems.forEach(item => {
    const total = (item.allocations || []).reduce((dollarSum, allocation) => {
      return dollarSum.add(getMoneyObj(allocation.dollars));
    }, getMoneyObj(0));

    item.program_cost = Number((total.amount / 100).toFixed(2));
  });
};

export const checkForOverAllocationBeforeAllocating = (allocationsArray) => {
  let itemCostAlreadyAdded = [];
  let totalItemsCost = (allocationsArray || []).reduce( (total, allocation) => {
    if (!itemCostAlreadyAdded.includes(allocation.itemId)) {
      itemCostAlreadyAdded.push(allocation.itemId);
      return total.add(
        getMoneyObj(Math.abs(Number(allocation.itemTotalCost)))
      );
    } else {
      return total.add(
        getMoneyObj(0)
      );
    }
  }, getMoneyObj(0));
  
  let totalProgramsCost = (allocationsArray || []).reduce( (total, allocation) => {
    const absoluteProgramCost = Math.abs(Number((allocation.programCost.amount / 100).toFixed(2)));
    return total.add(getMoneyObj(absoluteProgramCost));
  }, getMoneyObj(0),);

  return totalProgramsCost.greaterThan(totalItemsCost);;
};
